import React from 'react';
import { createPortal } from 'react-dom';
import {
  TIME_OFF_COLORS,
  TIME_OFF_LEGEND,
  describeEntry,
  formatRange,
  formatYmd,
  getCalendarLabel,
  getTimeOffChipStyle,
  isAbsence,
  isPartialDay,
} from '../utils/timeOffUtils';

/* ---------------------------------------------------------------------------
   Pieces shared by Calendar.js and EmbeddedCalendar.js so time off and
   attendance look identical on both.
--------------------------------------------------------------------------- */

export const TimeOffChip = ({ entry, onOpen }) => (
  <div
    role="button"
    tabIndex={0}
    style={getTimeOffChipStyle(entry)}
    title={getCalendarLabel(entry)}
    onClick={(e) => {
      e.stopPropagation();
      onOpen(entry);
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onOpen(entry);
      }
    }}
  >
    {getCalendarLabel(entry)}
  </div>
);

export const TimeOffLegendItems = () =>
  TIME_OFF_LEGEND.map((item) => (
    <div key={item.kind} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 4,
          background: TIME_OFF_COLORS[item.kind],
          borderLeft: '3px solid #fff',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
          display: 'inline-block',
        }}
      />
      <span>{item.label}</span>
    </div>
  ));

// "Who's out" strip for the day view - rendered once, above the time slots.
export const OutTodayList = ({ entries, onOpen }) => {
  if (!entries || entries.length === 0) return null;
  return (
    <div
      style={{
        margin: '10px 0 14px',
        padding: '10px 12px',
        border: '1px solid #fecaca',
        borderRadius: 10,
        background: '#fff7f7',
      }}
    >
      <strong style={{ display: 'block', marginBottom: 6, color: '#7f1d1d' }}>
        Out / partial day ({entries.length})
      </strong>
      {entries.map((entry) => (
        <TimeOffChip key={entry.id} entry={entry} onOpen={onOpen} />
      ))}
    </div>
  );
};

// Details popup. Shows WHAT happened and how much notice there was. The
// reason and notes are left to the Time Off pages, because the calendar is
// visible to the whole team.
export const TimeOffDetailsModal = ({ entry, onClose }) => {
  if (!entry) return null;
  const absence = isAbsence(entry);
  const partial = isPartialDay(entry);

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.55)',
          zIndex: 1999998,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          margin: 0,
          width: '90%',
          maxWidth: 480,
          maxHeight: '85vh',
          overflowY: 'auto',
          background: '#fff',
          padding: 20,
          borderRadius: 10,
          boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
          textAlign: 'left',
          boxSizing: 'border-box',
          zIndex: 1999999,
        }}
      >
        <h2 style={{ marginTop: 0 }}>{absence ? 'Attendance' : 'Time Off'}</h2>
        <p>
          <strong>Name:</strong> {entry.name || '—'}
        </p>
        <p>
          <strong>{partial ? 'Date:' : 'Dates:'}</strong>{' '}
          {partial ? formatYmd(entry.startDate) : formatRange(entry.startDate, entry.endDate)}
        </p>
        <p>
          <strong>{absence ? 'What happened:' : 'Type:'}</strong> {describeEntry(entry)}
        </p>
        {absence ? (
          <>
            <p>
              <strong>Notice given:</strong> {entry.noticeGiven || '—'}
            </p>
            {entry.notifiedAt && !entry.noCallNoShow && (
              <p>
                <strong>Told us:</strong> {new Date(entry.notifiedAt).toLocaleString()}
              </p>
            )}
            <p>
              <strong>Logged by:</strong> {entry.loggedBy || '—'}
            </p>
          </>
        ) : (
          <>
            <p>
              <strong>Paid time type:</strong> {entry.paidTimeType || '—'}
            </p>
            <p>
              <strong>Status:</strong> {entry.status || '—'}
            </p>
            {entry.dateRequested && (
              <p>
                <strong>Requested:</strong> {new Date(entry.dateRequested).toLocaleString()}
              </p>
            )}
          </>
        )}
        <p style={{ fontSize: 12, color: '#64748b' }}>
          Reasons and notes are on the Time Off Approvals page.
        </p>
        <div className="modal-buttons">
          <button className="red-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};
